import * as v from 'valibot';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/data/resources/tas-program-template.type';
import type { TASApplicationModelFieldsSignature } from 'tio-data/data/resources/tas-application.type';
import type { TASProgramInstanceModelFieldsSignature } from 'tio-data/data/resources/tas-program-instance.type';
import type {
  GenericTemplateField,
  CustomFieldSignature,
  TemplateApprovalWorkflowApprover,
} from '../../types/tuition-assistance';
import type { TASCourseModelFieldsSignature } from 'tio-data/data/resources/tas-course.type';
import type { TasFieldOption as TasFieldOptionModel } from 'tio-data/data/resources/tas-field-option.type';

/**
 * @remarks
 * This type serves as a catch-all for FieldsSignature types for the various models.
 * It serves a purpose for typing utility methods that operate on any one of the FieldsSignature types,
 * but it is not meant to be used as a type when you know which model you are working with.
 * In that case, you should use the specific FieldsSignature type for that model.
 */
export type TASFields =
  | TASProgramTemplateModelFieldsSignature
  | TASApplicationModelFieldsSignature
  | TASProgramInstanceModelFieldsSignature
  | TASCourseModelFieldsSignature;

export type FieldValue =
  string | boolean | number | null | Record<string, unknown> | TemplateApprovalWorkflowApprover;

export type TemplateFieldName = keyof TASProgramTemplateModelFieldsSignature;

export type FieldName =
  | TemplateFieldName
  | keyof TASApplicationModelFieldsSignature
  | keyof TASProgramInstanceModelFieldsSignature
  | keyof TASCourseModelFieldsSignature;

export type CustomFieldId =
  | `CUSTOM_FIELD_TAS_PROGRAM_INSTANCE_${number}`
  | `CUSTOM_FIELD_TAS_APPLICATION_${number}`
  | `CUSTOM_FIELD_TAS_COURSE_${number}`
  | null
  | undefined;

export function setSingleValueForTasField(
  fieldName: keyof TASProgramTemplateModelFieldsSignature,
  value: FieldValue,
  fieldsObject: TASProgramTemplateModelFieldsSignature
): void;
export function setSingleValueForTasField(
  fieldName: keyof TASProgramInstanceModelFieldsSignature,
  value: FieldValue,
  fieldsObject: TASProgramInstanceModelFieldsSignature
): void;
export function setSingleValueForTasField(
  fieldName: keyof TASApplicationModelFieldsSignature,
  value: FieldValue,
  fieldsObject: TASApplicationModelFieldsSignature
): void;
export function setSingleValueForTasField(
  fieldName: keyof TASCourseModelFieldsSignature,
  value: FieldValue,
  fieldsObject: TASCourseModelFieldsSignature
): void;
export function setSingleValueForTasField(
  fieldName: FieldName,
  value: FieldValue,
  fieldsObject: TASFields
): void {
  if (!fieldName || !fieldsObject) {
    return;
  }

  // @ts-expect-error - Cory this is the issue that is causing so many problems...
  // Element implicitly has an 'any' type because expression of type 'FieldName' can't be used to index type 'TASFields'.
  const existingFieldConfig = fieldsObject[fieldName] || {};

  const updatedValues = value === undefined ? [] : [value];

  // @ts-expect-error Figure out how to type to avoid this
  // "Type 'FieldValue' is not assignable to type 'never'. Type 'null' is not assignable to type 'never'."
  fieldsObject[fieldName] = { ...existingFieldConfig, values: updatedValues };
}

/**
 * Set the `values` array of a field on a fields EDITING COPY (a `trackedObject`
 * or a plain `{ ...record.fields }` clone that is reassigned onto the record
 * before save), preserving any other config already on that field. The
 * in-place, array-valued counterpart to
 * {@link setSingleValueForTasField} — used to bulk-seed a template's editable
 * fields from a plain key→value(s) map.
 *
 * `TASFields` is a heterogeneous per-key record, so a write indexed by a runtime
 * key lands on `never`; we widen to a string-indexable bag for the assignment —
 * the same localized cast used by {@link setTrackedTasFieldValues}. This keeps
 * the unsoundness in one reviewed place instead of an inline suppression at
 * every program-builder call site.
 */
export function setValuesForTasField<F extends TASFields>(
  fieldsObject: F,
  fieldName: keyof F,
  values: FieldValue[]
): void {
  if (!fieldName || !fieldsObject) {
    return;
  }
  const bag = fieldsObject as Record<keyof F, Record<string, unknown>>;
  bag[fieldName] = { ...bag[fieldName], values };
}

/**
 * WarpDrive-safe single-field write for a LIVE record.
 *
 * `setSingleValueForTasField` mutates the `fields` object in place, which
 * WarpDrive's cache does not track: the change is dropped from `serializePatch`
 * on save and is not reactive. This helper clones `record.fields`, applies the
 * value, then REASSIGNS `record.fields` so the write goes through `setAttr` and
 * is persisted. Use it instead of `setSingleValueForTasField(name, value,
 * record.fields)` whenever the target is a live record attribute rather than a
 * `trackedObject` editing copy that is reassigned onto the record before save.
 *
 * Calls accumulate: each invocation clones the just-reassigned `fields`, so
 * setting several values in sequence on the same record is safe.
 */
export function setTrackedTasFieldValue<F extends TASFields>(
  record: { fields: F },
  fieldName: keyof F,
  value: FieldValue
): void {
  const fields = { ...record.fields } as F;
  // `fieldName: keyof F` gives callers per-record safety (you can't set an
  // application field on a course/template record). The casts below are an
  // internal detail: `setSingleValueForTasField`'s public overloads can't
  // resolve against the generic union `F`, and its body is type-agnostic at
  // runtime, so we route through the application overload.
  setSingleValueForTasField(
    fieldName as unknown as keyof TASApplicationModelFieldsSignature,
    value,
    fields as unknown as TASApplicationModelFieldsSignature
  );
  record.fields = fields;
}

/**
 * WarpDrive-safe MULTI-value write for a LIVE record — the array-valued
 * counterpart to {@link setTrackedTasFieldValue}. Repeating fields (e.g.
 * `SCHOLARSHIPS_RECEIVED`) store every entry under a single field's `values`
 * array, so they can't go through `setSingleValueForTasField`, which wraps a
 * lone value as `values: [value]`. This clones `record.fields`, replaces the
 * target field's `values` with the supplied array (preserving any other config
 * on that field), then REASSIGNS `record.fields` so the write goes through
 * `setAttr` and is persisted. Mutating `record.fields[fieldName]` in place is
 * not tracked by the cache and is dropped from `serializePatch` on save.
 *
 * Calls accumulate: each invocation clones the just-reassigned `fields`, so
 * setting several fields in sequence on the same record is safe.
 */
export function setTrackedTasFieldValues<F extends TASFields>(
  record: { fields: F },
  fieldName: keyof F,
  values: FieldValue[]
): void {
  const fields = { ...record.fields } as F;
  // Cast to a string-indexable bag: the generic union `TASFields` collapses each
  // field's value type to `never` for writes, so we widen the access to index it.
  const bag = fields as Record<keyof F, Record<string, unknown>>;
  bag[fieldName] = { ...bag[fieldName], values };
  record.fields = fields;
}

/**
 * WarpDrive-safe merge of custom-field value updates into an existing custom
 * fields list. Custom fields persist via the `customFields` ARRAY attribute on
 * TAS records, and WarpDrive only includes an attribute in `serializePatch`
 * when its value is REASSIGNED to a new reference — so callers MUST do
 * `record.customFields = mergeCustomFieldValues(record.customFields, updates)`.
 *
 * This is pure: it never mutates `existing` or its objects. It returns a brand
 * new array of shallow-cloned field objects, with each update merged onto the
 * matching field by `label` + `province`. Updates with no match are appended
 * only when `appendNew` is true (the application/instance edit flows build the
 * list and append; the approver review flow edits existing fields only).
 */
export function mergeCustomFieldValues<T extends { label?: string; province?: string }>(
  existing: T[] | null | undefined,
  updates: T[],
  options: { appendNew?: boolean } = {}
): T[] {
  const result: T[] = (existing ?? []).map((field) => ({ ...field }));
  for (const update of updates) {
    const index = result.findIndex(
      (field) => field.label === update.label && field.province === update.province
    );
    if (index >= 0) {
      result[index] = { ...update };
    } else if (options.appendNew) {
      result.push({ ...update });
    }
  }
  return result;
}

export function updateTasTemplateFieldConfig(
  fieldName: TemplateFieldName,
  updatedObject: Partial<GenericTemplateField>,
  fieldsObject: TASProgramTemplateModelFieldsSignature
) {
  if (!fieldName || !fieldsObject) {
    return;
  }

  const existingFieldConfig = fieldsObject[fieldName] || {};

  // @ts-expect-error Figure out how to type so it can properly index the keys
  // "Element implicitly has an 'any' type because expression of type 'FieldName' can't be used to index type 'TASFields'."
  fieldsObject[fieldName] = { ...existingFieldConfig, ...updatedObject };
}

export function getSingleValueForTasField<
  T extends TASFields,
  F extends TASCourseModelFieldsSignature | FieldValue | undefined,
>(fieldName: FieldName, fieldsObject: T | undefined, defaultValue?: F): F {
  if (!fieldName || !fieldsObject) {
    return defaultValue as F;
  }
  // @ts-expect-error: Typescript cannot guarantee that a union of keys (from FieldName) will always match the union of object types (TASFields).
  // This was dealt with partially using the exported overloads above, which will enforce valid key/value pairs in the consuming code.
  // Yet this fallback TS error still happens.
  const value = fieldsObject[fieldName]?.values?.[0];

  if (value === undefined) {
    return defaultValue as F;
  }

  return value as F;
}

export function getAllValuesForTasField(
  fieldName: keyof TASProgramTemplateModelFieldsSignature,
  fieldsObject: TASProgramTemplateModelFieldsSignature
): FieldValue[];
export function getAllValuesForTasField(
  fieldName: keyof TASProgramInstanceModelFieldsSignature,
  fieldsObject: TASProgramInstanceModelFieldsSignature
): FieldValue[];
export function getAllValuesForTasField(
  fieldName: keyof TASApplicationModelFieldsSignature,
  fieldsObject: TASApplicationModelFieldsSignature
): FieldValue[];
export function getAllValuesForTasField(
  fieldName: keyof TASCourseModelFieldsSignature,
  fieldsObject: TASCourseModelFieldsSignature
): FieldValue[];
export function getAllValuesForTasField(fieldName: FieldName, fieldsObject: TASFields) {
  if (!fieldName || !fieldsObject) {
    return [];
  }

  // @ts-expect-error: Typescript cannot guarantee that a union of keys (from FieldName) will always match the union of object types (TASFields).
  // This was dealt with partially using the exported overloads above, which will enforce valid key/value pairs in the consuming code.
  // Yet this fallback TS error still happens.
  return fieldsObject[fieldName]?.values || [];
}

/**
 * This function is used to get the config for a field on a program template.
 * It is used in the program builder to display the field config in the UI.
 * For convenience, it also returns the first value in the values array as `value`.
 * The return value is readonly.
 *
 * @param fieldName
 * @param fieldsObject
 * @returns TemplateFieldConfigAndValues
 *
 */

export type ReadonlyTemplateFieldConfigAndValues = Readonly<
  GenericTemplateField & { value: FieldValue | undefined; name: TemplateFieldName }
>;

function normalizeComponentTypeHint(componentType: unknown): string | undefined {
  if (typeof componentType !== 'string') {
    return undefined;
  }

  const normalizedHint = componentType.trim().toLowerCase();
  return normalizedHint.length > 0 ? normalizedHint : undefined;
}

function extractFieldComponentTypeHint(fieldConfig: unknown): string | undefined {
  if (!fieldConfig || typeof fieldConfig !== 'object') {
    return undefined;
  }

  const config = fieldConfig as Record<string, unknown>;
  const directHint = normalizeComponentTypeHint(config.component_type ?? config.componentType);
  if (directHint) {
    return directHint;
  }

  const uiHint = config.ui_hint ?? config.uiHint;
  if (!uiHint || typeof uiHint !== 'object') {
    return undefined;
  }

  const hintConfig = uiHint as Record<string, unknown>;
  return normalizeComponentTypeHint(hintConfig.component_type ?? hintConfig.componentType);
}

export function resolveTasFieldComponentType(
  fieldName: TemplateFieldName,
  fieldsObject: TASProgramTemplateModelFieldsSignature,
  explicitHint?: string
): string | undefined {
  const normalizedExplicitHint = normalizeComponentTypeHint(explicitHint);
  if (normalizedExplicitHint) {
    return normalizedExplicitHint;
  }

  const metadataHint = extractFieldComponentTypeHint(fieldsObject?.[fieldName]);
  if (metadataHint) {
    return metadataHint;
  }

  if (fieldName === 'SCHOOL_INSTITUTION_NAME') {
    return 'school-select';
  }

  return undefined;
}

export function isTasFieldComponentType(
  fieldName: TemplateFieldName,
  fieldsObject: TASProgramTemplateModelFieldsSignature,
  componentType: string,
  explicitHint?: string
) {
  const normalizedComponentType = normalizeComponentTypeHint(componentType);
  if (!normalizedComponentType) {
    return false;
  }

  return (
    resolveTasFieldComponentType(fieldName, fieldsObject, explicitHint) === normalizedComponentType
  );
}

export function getConfigForTasField(
  fieldName: TemplateFieldName,
  fieldsObject: TASProgramTemplateModelFieldsSignature
): ReadonlyTemplateFieldConfigAndValues {
  if (!fieldName || !fieldsObject || !fieldsObject[fieldName]) {
    console.warn(`Unable to resolve config for TAS field ${fieldName}. Returning empty config.`);
    return {
      name: fieldName,
      label: '',
      province: 'PROGRAM',
      required: false,
      visible: false,
      values: [],
      value: undefined,
    };
  }
  const config = fieldsObject[fieldName] as GenericTemplateField;
  return { ...config, value: config.values?.[0], name: fieldName };
}

/**
 * When a TasProgramInstance, TasApplication, or TasCourse is created, we copy the relevant custom fields
 * from the program template and set them on the model at the time of creation.
 * (We copy these arrays from `instanceCustomFields`, `applicationCustomFields`, and `courseCustomFields` on TasProgramTemplate model)
 *
 * This does not handle the edge case where the custom fields on the parent TasProgramTemplate might be edited after
 * the child instance/application/course has already been created. We need to preserve the values they might have stored, but update
 * the overall array of fields to include new ones or any changes to requirements.
 *
 * IMPORTANT:
 * The arrays of fields passed as arguments to this function should already be filtered by the proper model type. For example,
 * if we are passing in `TasProgramInstance.customFields` as `existingFields`, then we should pass in `TasProgramTemplate.instanceCustomFields`
 * as the `templateCustomFields`. See mapping below:
 *         `TasProgramInstance.customFields` and `TasProgramTemplate.instanceCustomFields`
 *         `TasApplication.customFields` and `TasProgramTemplate.applicationCustomFields`
 *         `TasCourse.customFields` and `TasProgramTemplate.courseCustomFields`
 *
 */
export function copyFieldsAndUpdatePerProgramTemplate(
  existingCustomFields: CustomFieldSignature[] = [],
  templateCustomFields: CustomFieldSignature[] = []
): CustomFieldSignature[] {
  // Make a clean copy of the existing custom fields
  const resolvedFields = existingCustomFields.map((field) => {
    return { ...field };
  });

  // Loop through the custom fields defined by the program template
  // and check to see if they exist already.
  templateCustomFields.forEach((programField) => {
    const existingField = resolvedFields.find((instanceField) => {
      // TAS.TODO: This is extremely brittle. We need to add a more unique ID to custom fields in the program builder.
      return instanceField.label === programField.label;
    });

    // If the field does not exist yet, add it to the resolved fields.
    // Otherwise, update the existing field with the config from program template
    // but preserve the values stored.
    if (!existingField) {
      resolvedFields.push({ ...programField });
    } else {
      const indexOfMatchingField = resolvedFields.indexOf(existingField);
      resolvedFields[indexOfMatchingField] = { ...programField, values: existingField.values };
    }
  });

  // We are purposely not going to remove old custom fields that might have been present on the program template but were deleted
  // from the program config. This ensures that we do not lose information that a user might have input that might be relevant
  // to the timeframe in which their instance/application/course exists.

  return resolvedFields;
}

export function getCustomFieldObjectsForProvince(
  province: string,
  customFieldsArray: CustomFieldSignature[]
): CustomFieldSignature[] {
  // Filter customFields by province, sort by ordinal, map and return only necessary keys
  const customFields = customFieldsArray
    .filter((obj) => obj.province === province)
    .sort((a, b) => a.ordinal - b.ordinal);

  return customFields;
}

export type ValidationField = {
  name: keyof TASProgramTemplateModelFieldsSignature;
  rules?: Record<string, unknown>;
  errors?: Record<string, unknown>;
};

/**
 * Error result compatible with the errorsForField helper (Yup ValidationError format).
 */
export type ValidationErrorResult = {
  errors: string[];
  value: string;
  path: string;
};

/**
 * Validates data against one or more valibot schemas and returns errors
 * in the errorsForField-compatible format.
 *
 * Replaces the try/catch validateSync + err.inner pattern used with yup.
 */
export function validateAndCollectErrors(
  data: Record<string, unknown>,
  ...schemas: (v.GenericSchema | null | undefined)[]
): ValidationErrorResult[] {
  const errorMap = new Map<string, string[]>();

  for (const schema of schemas) {
    if (!schema) continue;
    const result = v.safeParse(schema, data);
    if (!result.success) {
      const flat = v.flatten(result.issues);
      if (flat.nested) {
        for (const [key, messages] of Object.entries(flat.nested)) {
          if (!messages) continue;
          const existing = errorMap.get(key) || [];
          existing.push(...messages);
          errorMap.set(key, existing);
        }
      }
    }
  }

  return Array.from(errorMap.entries()).map(([path, errors]) => ({
    path,
    errors,
    value: String(data[path] ?? ''),
  }));
}

type PropertyRules = Record<string, unknown> & {
  type?: string;
  required?: boolean;
  min?: unknown;
  max?: unknown;
  typeError?: string;
};

type CrossFieldCheck = {
  field: string;
  refField: string;
  direction: 'min' | 'max';
  message: string;
};

/** Schema that accepts both string and Date values (date fields may arrive as either) */
const dateOrString = v.union([v.string(), v.date()]);
type DateOrString = string | Date;

/** Normalize a string-or-Date value to a Date for comparison */
function toDate(val: DateOrString): Date {
  return val instanceof Date ? val : new Date(val);
}

/**
 * Builds a valibot schema for a single field based on JSON Schema-style rules.
 */
function buildValibotFieldSchema(
  rules: PropertyRules,
  isRequired: boolean,
  messages: Record<string, string>
): v.GenericSchema {
  const type = rules.type || 'string';
  const requiredMsg = messages.required || 'Required';
  const typeError = rules.typeError as string | undefined;

  switch (type) {
    case 'string': {
      return isRequired
        ? v.pipe(v.string(requiredMsg), v.nonEmpty(requiredMsg))
        : v.optional(v.string());
    }
    case 'number': {
      const msg = typeError || requiredMsg;
      let base: v.GenericSchema;

      if (typeof rules.min === 'number' && typeof rules.max === 'number') {
        base = v.pipe(
          v.number(msg),
          v.minValue(rules.min, messages.min || `Must be at least ${rules.min}`),
          v.maxValue(rules.max, messages.max || `Must be at most ${rules.max}`)
        );
      } else if (typeof rules.min === 'number') {
        base = v.pipe(
          v.number(msg),
          v.minValue(rules.min, messages.min || `Must be at least ${rules.min}`)
        );
      } else if (typeof rules.max === 'number') {
        base = v.pipe(
          v.number(msg),
          v.maxValue(rules.max, messages.max || `Must be at most ${rules.max}`)
        );
      } else {
        base = v.number(msg);
      }

      return isRequired ? base : v.optional(base);
    }
    case 'date': {
      // Date fields may arrive as strings or Date instances
      if (isRequired) {
        return v.pipe(
          dateOrString,
          v.check((val) => (typeof val === 'string' ? val.length > 0 : true), requiredMsg)
        );
      }
      return v.optional(dateOrString);
    }
    case 'boolean': {
      return isRequired ? v.boolean(requiredMsg) : v.optional(v.boolean());
    }
    default:
      return v.optional(v.unknown());
  }
}

/**
 * Converts JSON Schema-style field properties into a valibot ObjectSchema.
 * Replaces `buildYup()` from `schema-to-yup`.
 *
 * Supports field types: string, number, date, boolean.
 * Handles cross-field date references (e.g., min: 'OTHER_FIELD_NAME').
 */
function jsonSchemaToValibot(
  jsonSchema: {
    properties: Record<string, PropertyRules>;
    required?: string[];
  },
  config: { errMessages?: Record<string, Record<string, string>> } = {}
): v.GenericSchema {
  const properties = jsonSchema.properties || {};
  const required = new Set(jsonSchema.required || []);
  const errMessages = config.errMessages || {};
  const propertyNames = Object.keys(properties);
  const entries: Record<string, v.GenericSchema> = {};
  const crossFieldChecks: CrossFieldCheck[] = [];

  for (const [fieldName, rules] of Object.entries(properties)) {
    const isRequired = required.has(fieldName);
    const fieldMessages = (errMessages[fieldName] || {}) as Record<string, string>;

    if (rules.type === 'date') {
      // Detect cross-field date references
      if (typeof rules.min === 'string' && propertyNames.includes(rules.min)) {
        crossFieldChecks.push({
          field: fieldName,
          refField: rules.min,
          direction: 'min',
          message: fieldMessages.min || `Must be after ${rules.min}`,
        });
      }
      if (typeof rules.max === 'string' && propertyNames.includes(rules.max)) {
        crossFieldChecks.push({
          field: fieldName,
          refField: rules.max,
          direction: 'max',
          message: fieldMessages.max || `Must be before ${rules.max}`,
        });
      }

      // Non-cross-field date constraints
      const minIsCrossField = typeof rules.min === 'string' && propertyNames.includes(rules.min);
      const maxIsCrossField = typeof rules.max === 'string' && propertyNames.includes(rules.max);
      const hasNonCrossMin = rules.min != null && !minIsCrossField;
      const hasNonCrossMax = rules.max != null && !maxIsCrossField;

      if (hasNonCrossMin || hasNonCrossMax) {
        const checkActions: v.CheckAction<DateOrString, string>[] = [];
        if (hasNonCrossMin) {
          const minDate = rules.min instanceof Date ? rules.min : new Date(String(rules.min));
          checkActions.push(
            v.check((val: DateOrString) => {
              if (typeof val === 'string' && !val) return true;
              return toDate(val) >= minDate;
            }, fieldMessages.min || 'Date is too early')
          );
        }
        if (hasNonCrossMax) {
          const maxDate = rules.max instanceof Date ? rules.max : new Date(String(rules.max));
          checkActions.push(
            v.check((val: DateOrString) => {
              if (typeof val === 'string' && !val) return true;
              return toDate(val) <= maxDate;
            }, fieldMessages.max || 'Date is too late')
          );
        }

        const requiredMsg = fieldMessages.required || 'Required';
        if (isRequired && checkActions.length === 2) {
          entries[fieldName] = v.pipe(
            dateOrString,
            v.check((val) => (typeof val === 'string' ? val.length > 0 : true), requiredMsg),
            checkActions[0]!,
            checkActions[1]!
          );
        } else if (isRequired && checkActions.length === 1) {
          entries[fieldName] = v.pipe(
            dateOrString,
            v.check((val) => (typeof val === 'string' ? val.length > 0 : true), requiredMsg),
            checkActions[0]!
          );
        } else if (isRequired) {
          entries[fieldName] = v.pipe(
            dateOrString,
            v.check((val) => (typeof val === 'string' ? val.length > 0 : true), requiredMsg)
          );
        } else if (checkActions.length === 2) {
          entries[fieldName] = v.optional(v.pipe(dateOrString, checkActions[0]!, checkActions[1]!));
        } else if (checkActions.length === 1) {
          entries[fieldName] = v.optional(v.pipe(dateOrString, checkActions[0]!));
        } else {
          entries[fieldName] = v.optional(dateOrString);
        }
      } else {
        entries[fieldName] = buildValibotFieldSchema(rules, isRequired, fieldMessages);
      }
    } else {
      entries[fieldName] = buildValibotFieldSchema(rules, isRequired, fieldMessages);
    }
  }

  const baseSchema = v.object(entries);

  if (crossFieldChecks.length === 0) {
    return baseSchema;
  }

  // Add cross-field date checks using v.forward
  const forwardActions = crossFieldChecks.map((check) =>
    v.forward(
      v.check((input: Record<string, unknown>) => {
        const fieldVal = input[check.field];
        const refVal = input[check.refField];
        if (!fieldVal || !refVal) return true;
        const fieldDate = toDate(fieldVal as DateOrString);
        const refDate = toDate(refVal as DateOrString);
        if (isNaN(fieldDate.getTime()) || isNaN(refDate.getTime())) return true;
        return check.direction === 'min' ? fieldDate >= refDate : fieldDate <= refDate;
      }, check.message),
      [check.field]
    )
  );

  if (forwardActions.length === 1) {
    return v.pipe(baseSchema, forwardActions[0]!);
  }
  if (forwardActions.length === 2) {
    return v.pipe(baseSchema, forwardActions[0]!, forwardActions[1]!);
  }
  // Fallback for 3+ cross-field checks (unlikely but safe)
  return v.pipe(baseSchema, ...forwardActions);
}

export function buildValidationSchemaForProgramTemplateFields(
  fieldsToValidate: ValidationField[] = [],
  programTemplate: TasProgramTemplateModel
): v.GenericSchema {
  const fieldsConfig: TASProgramTemplateModelFieldsSignature = programTemplate?.fields || {};

  try {
    const properties: Record<string, PropertyRules> = {};
    const errMessages: Record<string, Record<string, string>> = {};
    const requiredFieldNames: string[] = [];

    fieldsToValidate.forEach(({ name, rules = {}, errors = {} }) => {
      const isRequired = !!fieldsConfig[name]?.required;
      const isVisible = !!fieldsConfig[name]?.visible;
      if (!isVisible) {
        return;
      }
      properties[name] = {
        required: isRequired,
        ...rules,
      };
      if (isRequired) {
        requiredFieldNames.push(name);
        const message = fieldsConfig[name]?.label
          ? `${fieldsConfig[name]?.label} is required`
          : 'Required';
        errMessages[name] = {
          required: message,
          ...(errors as Record<string, string>),
        };
      } else {
        errMessages[name] = { ...(errors as Record<string, string>) };
      }
    });

    return jsonSchemaToValibot({ properties, required: requiredFieldNames }, { errMessages });
  } catch (e) {
    console.error('Error setting up validation schema for program template fields', e, {
      fieldsToValidate,
      programTemplateId: programTemplate?.id,
      fieldsConfig,
    });
    return v.object({});
  }
}

export function buildValidationSchemaForCustomFields(
  fieldsToValidate: CustomFieldSignature[] = []
): v.GenericSchema {
  try {
    const properties: Record<string, PropertyRules> = {};
    const errMessages: Record<string, Record<string, string>> = {};
    const requiredFieldNames: string[] = [];

    const fields = fieldsToValidate.map((field) => {
      return {
        name: field.label, // Label is our only unique ID on these. Need to introduce unique identifier.
        label: field.label,
        required: field.required,
        rules: (field.validation?.yup?.rules || {}) as Record<string, unknown>,
        errors: (field.validation?.yup?.errors || {}) as Record<string, string>,
      };
    });

    fields.forEach(({ name, label, required, rules = {}, errors = {} }) => {
      const isRequired = !!required;
      properties[name] = {
        required: isRequired,
        ...rules,
      };
      if (isRequired) {
        requiredFieldNames.push(name);
        const message = label ? `${label} is required` : 'Required';
        errMessages[name] = {
          required: message,
          ...errors,
        };
      } else {
        errMessages[name] = { ...errors };
      }
    });

    return jsonSchemaToValibot({ properties, required: requiredFieldNames }, { errMessages });
  } catch (_e) {
    console.error('Error setting up validation schema for custom fields', {
      fieldsToValidate,
    });
    return v.object({});
  }
}

export function buildCustomFieldsFormModelForValidation(customFields: CustomFieldSignature[] = []) {
  const entries = customFields.map(({ label, values }) => {
    const keyValue = values.length > 1 ? values : values[0];
    const keyName = label;
    return [keyName, keyValue];
  });

  return Object.fromEntries(entries);
}

export function getSortedOptionsByFieldName(
  fieldName: TemplateFieldName | CustomFieldId,
  fieldOptions: TasFieldOptionModel[] = []
): TasFieldOptionModel[] {
  return fieldOptions
    .slice()
    .filter((option) => option.fieldName === fieldName)
    .sort((a, b) => {
      if (a.ordinal === b.ordinal) {
        return 0;
      }

      return a.ordinal > b.ordinal ? 1 : -1;
    });
}

export function getFieldLabelForFieldName(
  fieldName: TemplateFieldName,
  fieldsObject: TASProgramTemplateModelFieldsSignature
): string {
  return fieldsObject[fieldName]?.label;
}

export default null; // silence a false warning
